import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, TrendingUp, Play, Heart, Crown, LayoutGrid, Brain, Palette, Music, Shapes, BookOpen, Puzzle, Sparkles, ListOrdered, Cat, Boxes, BookA, Car, PaintBucket, Gamepad2, Lightbulb, Languages } from 'lucide-react';
import AppHeader from './components/AppHeader';
import BottomNavBar from './components/BottomNavBar';
import GameCard, { GameType, CategoryType } from './components/GameCard';
import SEOMeta from './components/SEOMeta';
import LoginPromptModal from './components/LoginPromptModal';
import PremiumUpgradeModal from './components/PremiumUpgradeModal';
import { useDongengFavorites } from './hooks/useDongengFavorites';
import { useAuth } from './contexts/AuthContext';

interface DongengVideo { id: string; title: string; thumbnail: string; published: string; }

/* ── Category "Semua" virtual entry ── */
const ALL_CATEGORY: CategoryType = {
  _id: 'semua', name: 'Semua', slug: 'semua',
  icon: 'grid_view', bgColor: '#f3e8ff', iconColor: '#9333ea',
};

// Map Material icon names to Lucide components
const ICON_MAP: Record<string, React.FC<any>> = {
  // Kategori Game (from DB)
  grid_view: LayoutGrid,       // Semua
  calculate: ListOrdered,      // Berhitung
  palette: PaintBucket,        // Warna
  pets: Cat,                   // Hewan
  shapes: Boxes,               // Bentuk
  abc: BookA,                  // Huruf
  auto_awesome: Sparkles,      // Logika
  language: Languages,         // Bahasa
  lightbulb: Lightbulb,        // Kreativitas
  directions_car: Car,         // Kendaraan
  draw: Palette,               // Mewarnai
  // Fallbacks
  psychology: Brain,
  music_note: Music,
  auto_stories: BookOpen,
  extension: Puzzle,
  sports_esports: Gamepad2,
};

/* ── Styled ─────────────────────────────────────────────────── */

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #ffffff;
  padding-bottom: 96px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

/* Hero */
const HeroSection = styled.section`
  padding: 16px;
`;

const HeroCard = styled.div`
  position: relative;
  min-height: 420px;
  border-radius: 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  text-align: center;
  padding: 28px 24px;
`;

const HeroBg = styled.img`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  object-position: center center;
  z-index: 0;
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 75%);
  z-index: 1;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

const HeroTitle = styled.h1`
  font-size: 34px;
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -0.5px;
  color: white;
  margin: 0;
  font-family: inherit;
`;

const HeroSubtitle = styled.p`
  font-size: 15px;
  font-weight: 500;
  color: rgba(255,255,255,0.88);
  margin: 0;
`;

const HeroCTA = styled.button`
  width: 100%;
  padding: 16px;
  background: #2DBCAF;
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 17px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(45,188,175,0.4);
  transition: opacity 0.2s;
  &:hover { opacity: 0.93; }
`;

/* Category Filter */
const CategoryScroll = styled.section`
  display: flex;
  gap: 14px;
  padding: 8px 16px 12px;
  overflow-x: auto;
  &::-webkit-scrollbar { display: none; }
`;

const CategoryBtn = styled.button<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
`;

const CategoryIconBox = styled.div<{ active?: boolean; bgColor: string }>`
  width: 56px; height: 56px;
  border-radius: 16px;
  background: ${p => p.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${p => p.active ? '#2DBCAF' : 'transparent'};
  transition: border 0.2s;
  svg { width: 28px; height: 28px; }
`;

const CategoryLbl = styled.span<{ active?: boolean }>`
  font-size: 11px;
  font-weight: ${p => p.active ? '800' : '600'};
  color: ${p => p.active ? '#0f172a' : '#94a3b8'};
  font-family: inherit;
`;

/* Game Section */
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 10px;
  h2 {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.3px;
    color: #0f172a;
    margin: 0;
    font-family: inherit;
  }
  a {
    font-size: 13px;
    font-weight: 700;
    color: #2DBCAF;
    cursor: pointer;
    text-decoration: none;
  }
`;

const GameGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 0 16px;
`;

/* Tumbuh Kembang Shortcuts */
const ShortcutsScroll = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 16px 16px;
  margin-top: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { display: none; }
`;

const ShortcutCard = styled.div<{ $color: string }>`
  position: relative;
  flex-shrink: 0;
  background: white;
  border-radius: 16px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border: 1.5px solid #f1f5f9;
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
  &:active { transform: scale(0.98); }
`;

const ShortcutPremiumBadge = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  background: linear-gradient(135deg, #FFA929 0%, #FF8C00 100%);
  color: white;
  font-size: 8px;
  font-weight: 700;
  padding: 2px 5px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 2px;
  box-shadow: 0 1px 4px rgba(255, 140, 0, 0.4);
  
  .material-symbols-outlined {
    font-size: 9px;
    font-variation-settings: 'FILL' 1;
  }
`;

const ShortcutIcon = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${p => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
  svg { width: 22px; height: 22px; }
`;

const ShortcutInfo = styled.div`
  h4 {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    white-space: nowrap;
  }
  p {
    font-size: 11px;
    color: #64748b;
    margin: 2px 0 0;
    white-space: nowrap;
  }
`;

/* Dongeng */
const DongengScroll = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 16px 8px 0;
  margin-left: 16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { display: none; }
`;

const DongengCard = styled.div`
  flex-shrink: 0;
  width: 136px;
  scroll-snap-align: start;
  cursor: pointer;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0,0,0,0.09);
  transition: transform 0.15s;
  &:hover { transform: translateY(-2px); }
`;

const DongengThumb = styled.div<{ $src: string }>`
  width: 136px;
  height: 190px;
  background: url(${p => p.$src}) center/cover no-repeat #e2e8f0;
  position: relative;
`;

const DongengPlayIcon = styled.div`
  position: absolute;
  bottom: 8px; right: 8px;
  width: 30px; height: 30px;
  border-radius: 50%;
  background: rgba(45,188,175,0.88);
  display: flex; align-items: center; justify-content: center;
  color: white;
  svg { width: 16px; height: 16px; }
`;

const DongengTitle = styled.p`
  display: none;
`;

const DongengHeartBtn = styled.button<{ $active?: boolean }>`
  position: absolute;
  top: 6px; right: 6px;
  width: 28px; height: 28px;
  border-radius: 50%;
  background: rgba(255,255,255,0.9);
  border: none;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; z-index: 5;
  box-shadow: 0 1px 4px rgba(0,0,0,0.15);
  padding: 0;
  color: ${p => p.$active ? '#ef4444' : '#94a3b8'};
  svg { width: 15px; height: 15px; }
`;

/* Premium */
const PremiumSection = styled.section`
  margin: 20px 16px 0;
`;

const PremiumCard = styled.div`
  background: linear-gradient(135deg, #FF8C00 0%, #FFA929 50%, #FFD700 100%);
  border-radius: 22px;
  padding: 22px 20px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(255, 169, 41, 0.35);
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 200px;
    height: 200px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
  }
`;

const PremiumTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  svg { width: 26px; height: 26px; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.2)); }
  h3 {
    font-size: 17px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0;
    font-family: inherit;
  }
`;

const PremiumDesc = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255,255,255,0.95);
  margin: 0 0 16px 0;
  line-height: 1.5;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const PremiumBtn = styled.button`
  background: white;
  color: #E07800;
  border: none;
  border-radius: 9999px;
  padding: 11px 28px;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  &:hover { opacity: 0.9; }
`;

/* Stats */
const StatsGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 16px 16px 0;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 16px 8px;
  text-align: center;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  border: 1px solid #f1f5f9;
  p:first-child {
    font-size: 24px;
    font-weight: 900;
    color: #2DBCAF;
    margin: 0 0 2px 0;
    font-family: inherit;
  }
  p:last-child {
    font-size: 10px;
    font-weight: 700;
    color: #AAA;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0;
    font-family: inherit;
  }
`;

const LoadingWrap = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #94a3b8;
  font-size: 14px;
`;

/* Footer */
const FooterSection = styled.footer`
  padding: 24px 16px 16px;
  text-align: center;
`;

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const FooterLink = styled.a`
  color: #64748b;
  font-size: 12px;
  text-decoration: none;
  &:hover {
    color: #2DBCAF;
    text-decoration: underline;
  }
`;

const FooterCopy = styled.p`
  margin: 12px 0 0;
  color: #94a3b8;
  font-size: 11px;
`;

/* ── Component ─────────────────────────────────────────────── */

function GameSelector() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if user has active premium
  const isPremium = user?.subscription?.status === 'active' && 
    user?.subscription?.endDate && 
    new Date(user.subscription.endDate) > new Date();
  const { isFavorite, toggleFavorite } = useDongengFavorites();
  const [games, setGames]       = useState<GameType[]>([]);
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [loading, setLoading]   = useState(true);
  const [activeCategoryId, setActiveCategoryId] = useState('semua');
  const [dongengVideos, setDongengVideos] = useState<DongengVideo[]>([]);
  const [showDongengLogin, setShowDongengLogin] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const handleDongengHeart = async (e: React.MouseEvent, videoId: string) => {
    e.stopPropagation();
    if (!user) { setShowDongengLogin(true); return; }
    await toggleFavorite(videoId);
  };

  useEffect(() => {
    Promise.all([
      fetch('/api/games').then(r => r.json()),
      fetch('/api/categories').then(r => r.json()),
    ])
      .then(([gamesData, catsData]: [GameType[], CategoryType[]]) => {
        setGames(gamesData);
        setCategories(catsData.filter((c: CategoryType) => c.slug !== 'semua'));
      })
      .catch(err => console.error('Failed to fetch:', err))
      .finally(() => setLoading(false));

    // Fetch dongeng videos (non-blocking)
    fetch('/api/dongeng/videos')
      .then(r => r.json())
      .then(d => { if (d.ok && d.videos?.length) setDongengVideos(d.videos.slice(0, 4)); })
      .catch(() => {});
  }, []);

  const allCategories = [ALL_CATEGORY, ...categories];

  const filteredGames = activeCategoryId === 'semua'
    ? games
    : games.filter(g => g.categories.some(c => c._id === activeCategoryId));

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Game Edukasi KinderGame.id',
    description: 'Daftar game edukasi interaktif untuk anak usia 3-6 tahun',
    numberOfItems: games.length,
    itemListElement: games.map((g, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: g.name,
      url: `https://kindergame.id${g.path}`,
    })),
  };

  const getCategoryIcon = (iconName: string, color: string) => {
    const IconComponent = ICON_MAP[iconName] || LayoutGrid;
    return <IconComponent style={{ color }} />;
  };

  return (
    <>
      <SEOMeta
        title="Platform Game Edukasi Interaktif untuk Anak Balita"
        description="200+ game edukasi seru untuk anak usia 3-6 tahun. Belajar huruf, angka, warna, hewan, logika sambil bermain. Gratis & aman!"
        path="/"
        jsonLd={jsonLd}
      />
      <PageWrapper>
        <AppHeader showCrown isPremium={isPremium} />

        {/* Hero */}
        <HeroSection>
          <HeroCard>
            <HeroBg src="/assets/images/hero-banner.png" alt="" />
            <HeroOverlay />
            <HeroContent>
              <HeroTitle>Main &amp; Belajar Jadi Lebih Seru!</HeroTitle>
              <HeroSubtitle>Platform edukasi interaktif untuk tumbuh kembang anak yang menyenangkan.</HeroSubtitle>
              <HeroCTA onClick={() => document.getElementById('game-section')?.scrollIntoView({ behavior: 'smooth' })}>
                Mulai Main Sekarang
              </HeroCTA>
            </HeroContent>
          </HeroCard>
        </HeroSection>

        {/* Tumbuh Kembang Shortcuts */}
        <ShortcutsScroll>
          <ShortcutCard 
            $color="#2DBCAF" 
            onClick={() => {
              if (!isPremium) {
                setShowPremiumModal(true);
              } else {
                navigate('/tumbuh-kembang/stimulasi');
              }
            }}
          >
            <ShortcutPremiumBadge>
              <span className="material-symbols-outlined">crown</span>
              Premium
            </ShortcutPremiumBadge>
            <ShortcutIcon $color="#2DBCAF">
              <Calendar />
            </ShortcutIcon>
            <ShortcutInfo>
              <h4>Agenda Stimulasi</h4>
              <p>Aktivitas harian</p>
            </ShortcutInfo>
          </ShortcutCard>
          <ShortcutCard $color="#3B82F6" onClick={() => navigate('/tumbuh-kembang/growth')}>
            <ShortcutIcon $color="#3B82F6">
              <TrendingUp />
            </ShortcutIcon>
            <ShortcutInfo>
              <h4>Grafik Pertumbuhan</h4>
              <p>Tracking TB & BB</p>
            </ShortcutInfo>
          </ShortcutCard>
          <ShortcutCard $color="#EF4444" onClick={() => navigate('/tumbuh-kembang/imunisasi')}>
            <ShortcutIcon $color="#EF4444">
              <span className="material-symbols-outlined" style={{ fontSize: 22 }}>vaccines</span>
            </ShortcutIcon>
            <ShortcutInfo>
              <h4>Checklist Imunisasi</h4>
              <p>Jadwal vaksin anak</p>
            </ShortcutInfo>
          </ShortcutCard>
          <ShortcutCard $color="#F59E0B" onClick={() => navigate('/tumbuh-kembang/mpasi')}>
            <ShortcutIcon $color="#F59E0B">
              <span className="material-symbols-outlined" style={{ fontSize: 22 }}>restaurant</span>
            </ShortcutIcon>
            <ShortcutInfo>
              <h4>Panduan MPASI</h4>
              <p>Menu 6 bln - 2 thn</p>
            </ShortcutInfo>
          </ShortcutCard>
        </ShortcutsScroll>

        {/* Category Filter */}
        <CategoryScroll>
          {allCategories.map(cat => (
            <CategoryBtn key={cat._id} active={activeCategoryId === cat._id} onClick={() => setActiveCategoryId(cat._id)}>
              <CategoryIconBox active={activeCategoryId === cat._id} bgColor={cat.bgColor}>
                {getCategoryIcon(cat.icon, cat.iconColor)}
              </CategoryIconBox>
              <CategoryLbl active={activeCategoryId === cat._id}>{cat.name}</CategoryLbl>
            </CategoryBtn>
          ))}
        </CategoryScroll>

        {/* Game Populer — max 4 */}
        <div id="game-section">
          <SectionHeader>
            <h2>Game Populer</h2>
            <a onClick={() => navigate('/games')}>Lihat Semua</a>
          </SectionHeader>
          {loading ? (
            <LoadingWrap>Memuat game...</LoadingWrap>
          ) : (
            <GameGrid>
              {filteredGames.slice(0, 4).map(game => (
                <GameCard key={game._id} game={game} />
              ))}
            </GameGrid>
          )}
        </div>

        {/* Dongeng Terbaru */}
        {dongengVideos.length > 0 && (
          <div>
            <SectionHeader>
              <h2>Dongeng Terbaru</h2>
              <a onClick={() => navigate('/dongeng')}>Lihat Semua</a>
            </SectionHeader>
            <DongengScroll>
              {dongengVideos.map(v => {
                const fav = isFavorite(v.id);
                return (
                  <DongengCard key={v.id} onClick={() => navigate('/dongeng', { state: { videoId: v.id } })}>
                    <DongengThumb $src={v.thumbnail}>
                      <DongengPlayIcon>
                        <Play fill="currentColor" />
                      </DongengPlayIcon>
                      <DongengHeartBtn
                        $active={fav}
                        onClick={e => handleDongengHeart(e, v.id)}
                      >
                        <Heart fill={fav ? 'currentColor' : 'none'} />
                      </DongengHeartBtn>
                    </DongengThumb>
                    <DongengTitle>{v.title}</DongengTitle>
                  </DongengCard>
                );
              })}
            </DongengScroll>
          </div>
        )}

        {/* Premium Banner - hide for premium users */}
        {!isPremium && (
          <PremiumSection>
            <PremiumCard>
              <PremiumTitle>
                <Crown fill="currentColor" />
                <h3>Premium Games</h3>
              </PremiumTitle>
              <PremiumDesc>
                Buka 100+ game eksklusif tanpa iklan dan akses fitur laporan belajar anak.
              </PremiumDesc>
              <PremiumBtn onClick={() => navigate('/premium')}>Langganan Sekarang</PremiumBtn>
            </PremiumCard>
          </PremiumSection>
        )}

        {/* Stats — game count from DB */}
        <StatsGrid>
          <StatCard><p>50k+</p><p>Pemain</p></StatCard>
          <StatCard><p>{loading ? '...' : `${games.length}+`}</p><p>Games</p></StatCard>
          <StatCard><p>4.8</p><p>Rating</p></StatCard>
        </StatsGrid>

        {/* Footer */}
        <FooterSection>
          <FooterLinks>
            <FooterLink href="/privacy-policy">Kebijakan Privasi</FooterLink>
            <FooterLink href="/terms">Syarat & Ketentuan</FooterLink>
            <FooterLink href="/contact">Hubungi Kami</FooterLink>
          </FooterLinks>
          <FooterCopy>© {new Date().getFullYear()} Kindergame. All rights reserved.</FooterCopy>
        </FooterSection>
      </PageWrapper>

      <BottomNavBar />
      {showPremiumModal && (
        <PremiumUpgradeModal 
          isOpen={showPremiumModal}
          onClose={() => setShowPremiumModal(false)} 
          featureName="Agenda Stimulasi"
        />
      )}
      
      {showDongengLogin && (
        <LoginPromptModal
          onLogin={() => { setShowDongengLogin(false); navigate('/profile'); }}
          onClose={() => setShowDongengLogin(false)}
          subtitle={<>Silakan login terlebih dahulu untuk<br />menyimpan dongeng favoritmu.</>}
        />
      )}
    </>
  );
}

export default GameSelector;
